import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Typography,
    Grid,
    Box,
    Avatar,
    Paper,
    Button,
    CircularProgress,
    IconButton,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Collapse,
    Fade
} from '@mui/material';
import api from '../api/axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import CategoryIcon from '@mui/icons-material/Category';
import PageAnimation from '../components/animations/PageAnimation';
import ProductCard from '../components/ProductCard';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import fruit from '../assets/myfruits.png';
import vegetable from '../assets/veggies.png';
// Helper to group products by category
const groupByCategory = (products) => {
    return products.reduce((acc, product) => {
        const category = product.category || 'Uncategorized';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(product);
        return acc;
    }, {});
};

function BrowsePage({ products = [], isLoggedIn, orderItems = {}, searchTerm = '', onClearSearch, onUpdateCart, onGuestAction, isLoading, onAiClick }) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    
    // Configure the number of bestsellers to show here
    const NUM_BESTSELLERS = 12;

    const [bestsellerIds, setBestsellerIds] = useState([]);

    useEffect(() => {
        const fetchBestsellers = async () => {
            try {
                const response = await api.get(`/bestsellers?limit=${NUM_BESTSELLERS}`);
                setBestsellerIds(response.data || []);
            } catch (err) {
                console.error("Failed to fetch bestsellers", err);
            }
        };
        fetchBestsellers();
    }, []);

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isVerySmall = useMediaQuery('(max-width:400px)');

    const userName = localStorage.getItem('contactPerson') || 'Guest';

    // --- Dynamic Header Text ---
    const headerTexts = [
        "Have a shopping list?",
        // "Send an audio order!",
        // "Order from past history!",
        // "Need recipe ingredients?",
        // "Check delivery status?",
        // "Reach out to support?",
        // "Special item requests?"
    ];

    const randomHeaderText = useMemo(() => {
        return headerTexts[Math.floor(Math.random() * headerTexts.length)];
    }, []);

    // --- Banner State ---
    const [showBanner, setShowBanner] = useState(true);
    const [timeLeft, setTimeLeft] = useState(7);

    useEffect(() => {
        if (timeLeft > 0 && showBanner) {
            const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timerId);
        } else if (timeLeft === 0) {
            setShowBanner(false);
        }
    }, [timeLeft, showBanner]);

    // --- Special Request State ---
    const [isRequestOpen, setIsRequestOpen] = useState(false);
    const [specialItemName, setSpecialItemName] = useState('');
    const [specialItemQty, setSpecialItemQty] = useState(1);
    const [specialItemUnit, setSpecialItemUnit] = useState('piece');
    const [requestLoading, setRequestLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

    const handleSpecialRequestSubmit = async () => {
        if (!specialItemName.trim()) {
            setNotification({ open: true, message: 'Please provide a description for the item.', severity: 'warning' });
            return;
        }
        if (!isLoggedIn) {
            onGuestAction();
            return;
        }

        setRequestLoading(true);

        try {
            const payload = {
                special_items: [{
                    name: specialItemName,
                    quantity: Number(specialItemQty),
                    mode: specialItemUnit
                }]
            };

            await api.post('/cart', payload);

            setNotification({ open: true, message: "Special item added to your cart!", severity: 'success' });
            setIsRequestOpen(false);
            setSpecialItemName('');
            setSpecialItemQty(1);
            setSpecialItemUnit('piece');

        } catch (err) {
            console.error("Failed to add special item:", err);
            setNotification({ open: true, message: "Could not add item. Please try again.", severity: 'error' });
        } finally {
            setRequestLoading(false);
        }
    };

    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') return;
        setNotification({ ...notification, open: false });
    };

    // --- NEW: Editing Order Popup ---
    const [editingOrderPopupOpen, setEditingOrderPopupOpen] = useState(false);
    const editingOrderId = location.state?.editingOrderId;

    useEffect(() => {
        if (editingOrderId) {
            setEditingOrderPopupOpen(true);
        }
    }, [editingOrderId]);


    // Memoize grouped products
    const groupedProducts = useMemo(() => groupByCategory(products), [products]);
    const categories = useMemo(() => Object.keys(groupedProducts).sort(), [groupedProducts]);

    // Calculate order items by product for ProductCard
    const orderItemsByProduct = useMemo(() => {
        const map = {};
        Object.values(orderItems || {}).forEach(item => {
            const pid = item.product?.id;
            if (!pid) return;
            if (!map[pid]) map[pid] = [];
            map[pid].push(item);
        });
        return map;
    }, [orderItems]);

    // Get "Bestsellers"
    const bestsellers = useMemo(() => {
        if (bestsellerIds.length > 0) {
            const actualBestsellers = bestsellerIds
                .map(id => products.find(p => p.id === id))
                .filter(Boolean);
            if (actualBestsellers.length > 0) {
                // The backend already sorts them by total_quantity descending
                return actualBestsellers.slice(0, NUM_BESTSELLERS);
            }
        }
        return products.slice(0, NUM_BESTSELLERS); // fallback if fetch fails or no matching products
    }, [products, bestsellerIds]);

    // Get "New Arrivals" (simulated: next 4 products)
    const newArrivals = useMemo(() => {
        return products.slice(4, 8);
    }, [products]);

    const handleCategoryClick = (category) => {
        navigate(`/category/${encodeURIComponent(category)}`);
    };

    // --- Search Logic ---
    const filteredProducts = useMemo(() => {
        if (!searchTerm) return [];
        const lower = searchTerm.toLowerCase().trim();
        return products.filter(p => p.name.toLowerCase().includes(lower));
    }, [products, searchTerm]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: '#75a84b' }} />
            </Box>
        );
    }

    if (!products || products.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
                <StorefrontIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">Catalog is loading or empty...</Typography>
            </Box>
        );
    }

    // If there is a search term, show search results ONLY
    if (searchTerm) {
        return (
            <>
                <PageAnimation>
                    <Box sx={{ pb: 8, bgcolor: '#fcfdfa', minHeight: '100vh', pt: 4 }}>
                        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
                            {/* Search Header */}
                            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconButton
                                    onClick={() => {
                                        if (location.hash === '#search-dropdown') {
                                            navigate(-1);
                                        } else {
                                            if (onClearSearch) onClearSearch();
                                            navigate('/browse');
                                        }
                                    }}
                                    sx={{ mr: 1, bgcolor: '#f0f0f0', '&:hover': { bgcolor: '#e0e0e0' } }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e3b1f' }}>
                                    Search Results for "{searchTerm}"
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                                    ({filteredProducts.length} items found)
                                </Typography>
                            </Box>

                            {filteredProducts.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                                    {/* Main No Results Card */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: { xs: 3, md: 5 },
                                            maxWidth: 500,
                                            width: '100%',
                                            textAlign: 'center',
                                            background: 'linear-gradient(135deg, #f9fbf7 0%, #edf5e6 100%)',
                                            borderRadius: '24px',
                                            border: '2px dashed #75a84b',
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* Decorative background circles */}
                                        <Box sx={{
                                            position: 'absolute',
                                            top: -50,
                                            right: -50,
                                            width: 150,
                                            height: 150,
                                            background: 'rgba(117, 168, 75, 0.08)',
                                            borderRadius: '50%',
                                        }} />
                                        <Box sx={{
                                            position: 'absolute',
                                            bottom: -30,
                                            left: -30,
                                            width: 100,
                                            height: 100,
                                            background: 'rgba(117, 168, 75, 0.06)',
                                            borderRadius: '50%',
                                        }} />

                                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                                            <Box sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: '#fff',
                                                borderRadius: '50%',
                                                p: 2,
                                                mb: 2,
                                                boxShadow: '0 4px 20px rgba(117, 168, 75, 0.2)',
                                            }}>
                                                <AutoAwesomeIcon sx={{ fontSize: 48, color: '#75a84b' }} />
                                            </Box>

                                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e3b1f', mb: 1 }}>
                                                Can't find "{searchTerm}"?
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.6 }}>
                                                No worries! Add it as a special item request and we'll source it for you.
                                            </Typography>

                                            <Button
                                                variant="contained"
                                                size="large"
                                                startIcon={<AddCircleOutlineIcon />}
                                                onClick={() => {
                                                    setSpecialItemName(searchTerm);
                                                    setIsRequestOpen(true);
                                                }}
                                                sx={{
                                                    bgcolor: '#75a84b',
                                                    color: 'white',
                                                    borderRadius: '50px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    fontSize: '1rem',
                                                    px: 4,
                                                    py: 1.5,
                                                    boxShadow: '0 6px 20px rgba(117, 168, 75, 0.35)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        bgcolor: '#5c8d36',
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 8px 25px rgba(117, 168, 75, 0.45)',
                                                    }
                                                }}
                                            >
                                                Request this Special Item
                                            </Button>

                                            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#9ca3af' }}>
                                                We'll do our best to fulfill your request!
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Box>
                            ) : (
                                <Grid container spacing={isMobile ? 2 : 3}>
                                    {filteredProducts.map((product) => (
                                        <Grid item xs={6} sm={4} md={3} lg={2.4} key={product.id}>
                                            <ProductCard
                                                product={product}
                                                orderItemsForProduct={orderItemsByProduct[product.id] || []}
                                                onUpdateCart={onUpdateCart}
                                                onGuestAction={onGuestAction}
                                                isLoggedIn={isLoggedIn}
                                                compact={isMobile}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Container>
                    </Box>
                </PageAnimation>

                {/* Special Request Dialog */}
                <Dialog
                    open={isRequestOpen}
                    onClose={() => setIsRequestOpen(false)}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: { borderRadius: 3, p: 1 }
                    }}
                >
                    <DialogTitle sx={{
                        fontWeight: 'bold',
                        color: '#75a84b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <AddCircleOutlineIcon /> Add Special Item
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 3, color: '#555' }}>
                            Can't find what you're looking for in the catalog? Add it here!
                            It will be added directly to your cart.
                        </DialogContentText>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    autoFocus
                                    label="Item Name & Description"
                                    placeholder="E.g. Large Watermelon, Brand X 500g..."
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={2}
                                    value={specialItemName}
                                    onChange={(e) => setSpecialItemName(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Quantity"
                                    type="number"
                                    fullWidth
                                    value={specialItemQty}
                                    onChange={(e) => setSpecialItemQty(e.target.value)}
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Unit / Mode</InputLabel>
                                    <Select
                                        value={specialItemUnit}
                                        label="Unit / Mode"
                                        onChange={(e) => setSpecialItemUnit(e.target.value)}
                                    >
                                        <MenuItem value="piece">Piece</MenuItem>
                                        <MenuItem value="case">Case</MenuItem>
                                        <MenuItem value="bag">Bag</MenuItem>
                                        <MenuItem value="lb">Lb (Weight)</MenuItem>
                                        <MenuItem value="box">Box</MenuItem>
                                        <MenuItem value="tray">Tray</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    * Price for special items will be determined and updated on the final invoice.
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Matax will do its best to fulfill this special request. If we’re unable to, we’ll notify you once we finish processing your order.
                                </Typography>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: '0 24px 20px' }}>
                        <Button onClick={() => setIsRequestOpen(false)} sx={{ color: '#666' }}>Cancel</Button>
                        <Button
                            onClick={handleSpecialRequestSubmit}
                            disabled={requestLoading}
                            variant="contained"
                            startIcon={requestLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                            sx={{
                                backgroundColor: '#75a84b',
                                px: 4,
                                py: 1,
                                '&:hover': { backgroundColor: '#7a8a54' }
                            }}
                        >
                            {requestLoading ? 'Adding...' : 'Add to Cart'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                        {notification.message}
                    </Alert>
                </Snackbar>
            </>
        );
    }



    return (
        <PageAnimation>
            <Box sx={{ pb: 8, bgcolor: '#fcfdfa', minHeight: '100vh', pt: 4 }}>
                {/* --- NEW: Editing Order Popup --- */}
                <Dialog
                    open={editingOrderPopupOpen}
                    onClose={() => setEditingOrderPopupOpen(false)}
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            p: 2,
                            textAlign: 'center'
                        }
                    }}
                >
                    <DialogTitle sx={{ color: '#75a84b', fontWeight: 'bold' }}>
                        Editing Order
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            You are currently editing <strong>Order #{editingOrderId ? editingOrderId.slice(-6).toUpperCase() : ''}</strong>.
                            Add or remove items as needed, then proceed to the cart to submit your changes.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => setEditingOrderPopupOpen(false)}
                            sx={{
                                bgcolor: '#75a84b',
                                color: 'white',
                                borderRadius: '20px',
                                px: 4
                            }}
                        >
                            Got it
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Hero / Welcome Section */}
                {/* <Box sx={{
                    background: 'linear-gradient(135deg, #75a84b 0%, #75a84b 100%)',
                    color: 'white',
                    pt: { xs: 4, md: 6 },
                    pb: { xs: 6, md: 8 },
                    px: { xs: 2, md: 4 },
                    borderRadius: { xs: '0 0 24px 24px', md: '0 0 40px 40px' },
                    mb: 4,
                    boxShadow: '0 4px 20px rgba(46, 59, 31, 0.2)'
                }}>
                <Container maxWidth="xl">
                        <Box sx={{ maxWidth: 800 }}>
                            <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 1.2 }}>
                                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                            </Typography>
                            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '2rem', md: '3rem' } }}>
                                Good day, {userName.split(' ')[0]}!
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 600 }}>
                                Fresh inventory has just arrived. What are we stocking your pantry with today?
                            </Typography>
                        </Box>
                    </Container>
                </Box> */}

                <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>

                    {/* Quick Actions Banner */}
                    <Collapse in={showBanner} timeout={1000} unmountOnExit>
                        <Fade in={showBanner} timeout={1000}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 3,
                                    bgcolor: 'white',
                                    borderRadius: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                    mb: 6,
                                    border: '1px solid rgba(0,0,0,0.05)',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{
                                        bgcolor: '#e8f5e9',
                                        p: 1.5,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <SmartToyIcon sx={{ color: '#75a84b' }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#2e3b1f', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                                            {randomHeaderText}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Use our AI assistant to order in seconds.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <CircularProgress
                                            variant="determinate"
                                            value={(timeLeft / 7) * 100}
                                            size={36}
                                            thickness={5}
                                            sx={{
                                                color: '#75a84b',
                                                bgcolor: '#e8f5e9',
                                                borderRadius: '50%'
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                top: 0,
                                                left: 0,
                                                bottom: 0,
                                                right: 0,
                                                position: 'absolute',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography variant="caption" component="div" sx={{ color: '#75a84b', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                                {timeLeft}s
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            if (onAiClick) {
                                                onAiClick();
                                            } else {
                                                navigate('/browse');
                                            }
                                        }}
                                        sx={{
                                            bgcolor: '#75a84b',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            borderRadius: 3,
                                            px: 4,
                                            textTransform: 'none',
                                            boxShadow: '0 4px 12px rgba(117, 168, 75, 0.3)',
                                            '&:hover': { bgcolor: '#6a9c42' }
                                        }}
                                    >
                                        Start Ordering
                                    </Button>
                                </Box>
                            </Paper>
                        </Fade>
                    </Collapse>

                    {/* Shop by Category Section - Modified for 3 items per row on mobile */}
                    {/* Shop by Category Section - Modified for 3 items per row on mobile */}
                    <Box sx={{ mb: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e3b1f', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CategoryIcon sx={{ color: '#75a84b' }} /> Browse Categories
                            </Typography>
                            {/* <Button
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate('/browse')}
                                sx={{ color: '#75a84b', textTransform: 'none', fontWeight: 600 }}
                            >
                                View Full Catalog
                            </Button> */}
                        </Box>

                        <Grid container spacing={2}>
                            {categories.map((category) => {
                                const product = groupedProducts[category]?.[0];

                                // 1. Define your custom image mapping here
                                const categoryCustomImages = {
                                    'Fruit': fruit,
                                    'Vegetable': vegetable,

                                };

                                // 2. Determine the image URL: 
                                // Priority: Custom Mapping -> Product Image -> Placeholder
                                const imageUrl = categoryCustomImages[category]
                                    || product?.image_url
                                    || 'https://via.placeholder.com/150';

                                return (
                                    <Grid item xs={4} sm={3} md={2} key={category}>
                                        <Box
                                            onClick={() => handleCategoryClick(category)}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-5px)',
                                                    '& .category-avatar': {
                                                        boxShadow: '0 8px 16px rgba(117, 168, 75, 0.25)',
                                                        borderColor: '#75a84b'
                                                    },
                                                    '& .category-text': {
                                                        color: '#75a84b'
                                                    }
                                                }
                                            }}
                                        >
                                            <Avatar
                                                className="category-avatar"
                                                src={imageUrl}
                                                alt={category}
                                                variant="rounded" // Changes shape from circular to rectangular with rounded corners
                                                sx={{
                                                    width: '100%',     // Makes it fill the grid width
                                                    height: { xs: 80, sm: 120 }, // Fixed height creates the rectangular aspect ratio
                                                    mb: 1.5,
                                                    borderRadius: '24px', // Use a specific pixel value for "extra" rounded corners
                                                    bgcolor: 'white',
                                                    border: '3px solid transparent',
                                                    boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                                    transition: 'all 0.3s ease',
                                                    maxWidth: { xs: 80, sm: 120 },
                                                    '& img': { objectFit: 'scale-down' } // Ensures the image fills the rectangle nicely
                                                }}
                                            />
                                            <Typography
                                                className="category-text"
                                                variant="caption"
                                                sx={{
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    color: '#444',
                                                    transition: 'color 0.2s',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {category}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                );
                            })}


                            {/* Special Request Item */}
                            <Grid item xs={4} sm={3} md={2}>
                                <Box
                                    onClick={() => setIsRequestOpen(true)}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            '& .category-avatar': {
                                                boxShadow: '0 8px 16px rgba(117, 168, 75, 0.25)',
                                                borderColor: '#75a84b'
                                            },
                                            '& .category-text': {
                                                color: '#75a84b'
                                            }
                                        }
                                    }}
                                >
                                    <Avatar
                                        className="category-avatar"
                                        variant="rounded"
                                        sx={{
                                            width: '100%',
                                            height: { xs: 80, sm: 120 },
                                            mb: 1.5,
                                            borderRadius: '24px',
                                            bgcolor: '#f9fbf7',
                                            border: '2px dashed #75a84b',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            transition: 'all 0.3s ease',
                                            maxWidth: { xs: 80, sm: 120 },
                                            minWidth: { xs: 80, sm: 120 },
                                            color: '#75a84b'
                                        }}
                                    >
                                        <AutoAwesomeIcon sx={{ fontSize: { xs: 30, sm: 40 } }} />
                                    </Avatar>
                                    <Typography
                                        className="category-text"
                                        variant="caption"
                                        sx={{
                                            fontWeight: '600',
                                            textAlign: 'center',
                                            color: '#444',
                                            transition: 'color 0.2s',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        Request Item
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Bestsellers Section */}
                    <Box sx={{ mb: 6 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: '#2e3b1f', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WhatshotIcon sx={{ color: '#ff9800' }} /> Bestsellers
                        </Typography>
                        <Grid container spacing={isMobile ? 2 : 3}>
                            {bestsellers.map((product) => (
                                <Grid item xs={6} sm={4} md={3} lg={2.4} key={product.id}>
                                    <ProductCard
                                        product={product}
                                        orderItemsForProduct={orderItemsByProduct[product.id] || []}
                                        onUpdateCart={onUpdateCart}
                                        onGuestAction={onGuestAction}
                                        isLoggedIn={isLoggedIn}
                                        compact={isMobile} // Pass compact attribute for mobile
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* Fresh Arrivals Section */}
                    {/* <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: '#2e3b1f', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalOfferIcon sx={{ color: '#75a84b' }} /> Farm Fresh Arrivals
                        </Typography>
                        <Grid container spacing={3}>
                            {newArrivals.map((product) => (
                                <Grid item xs={6} sm={4} md={3} lg={2.4} key={product.id}>
                                    <ProductCard
                                        product={product}
                                        orderItemsForProduct={orderItemsByProduct[product.id] || []}
                                        onUpdateCart={onUpdateCart}
                                        onGuestAction={onGuestAction}
                                        isLoggedIn={isLoggedIn}
                                        compact={isMobile} // Pass compact attribute for mobile
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box> */}

                </Container>
            </Box>

            {/* Special Request Dialog */}
            <Dialog
                open={isRequestOpen}
                onClose={() => setIsRequestOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 'bold',
                    color: '#75a84b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <AddCircleOutlineIcon /> Add Special Item
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 3, color: '#555' }}>
                        Can't find what you're looking for in the catalog? Add it here!
                        It will be added directly to your cart.
                    </DialogContentText>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                autoFocus
                                label="Item Name & Description"
                                placeholder="E.g. Large Watermelon, Brand X 500g..."
                                fullWidth
                                variant="outlined"
                                multiline
                                rows={2}
                                value={specialItemName}
                                onChange={(e) => setSpecialItemName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Quantity"
                                type="number"
                                fullWidth
                                value={specialItemQty}
                                onChange={(e) => setSpecialItemQty(e.target.value)}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Unit / Mode</InputLabel>
                                <Select
                                    value={specialItemUnit}
                                    label="Unit / Mode"
                                    onChange={(e) => setSpecialItemUnit(e.target.value)}
                                >
                                    <MenuItem value="piece">Piece</MenuItem>
                                    <MenuItem value="case">Case</MenuItem>
                                    <MenuItem value="bag">Bag</MenuItem>
                                    <MenuItem value="lb">Lb (Weight)</MenuItem>
                                    <MenuItem value="box">Box</MenuItem>
                                    <MenuItem value="tray">Tray</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                * Price for special items will be determined and updated on the final invoice.
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Matax will do its best to fulfill this special request. If we’re unable to, we’ll notify you once we finish processing your order.
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: '0 24px 20px' }}>
                    <Button onClick={() => setIsRequestOpen(false)} sx={{ color: '#666' }}>Cancel</Button>
                    <Button
                        onClick={handleSpecialRequestSubmit}
                        disabled={requestLoading}
                        variant="contained"
                        startIcon={requestLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                        sx={{
                            backgroundColor: '#75a84b',
                            px: 4,
                            py: 1,
                            '&:hover': { backgroundColor: '#7a8a54' }
                        }}
                    >
                        {requestLoading ? 'Adding...' : 'Add to Cart'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </PageAnimation>
    );
}

export default BrowsePage;
